/**
 * HPP final — HANYA OWNER (konstitusi: satu-satunya yang lihat HPP/margin).
 *
 * ⛔ KALIMAT DI KEPALA LAYAR ADALAH BAGIAN DARI KEPUTUSANNYA (K139):
 *    angka di sini biaya LANGSUNG (material + upah jahit), BUKAN biaya
 *    penuh — margin darinya margin KOTOR. Layar yang menyebutnya "HPP"
 *    tanpa keterangan itu akan dibaca sebagai biaya penuh, dan keputusan
 *    harga yang lahir darinya terlalu percaya diri.
 *
 * ⛔ TEMUAN TAMPIL DI ATAS ANGKA. HPP yang komponennya bolong (bahan tanpa
 *    snapshot harga, alokasi tanpa pembagi) tapi tampil mulus adalah margin
 *    yang berbohong manis — dan yang dibohongi pengambil keputusan harganya.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { tanggalLokalISO } from "../lib/format";
import Hint from "../components/etams/Hint";

const rupiah = (n) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR",
                                   maximumFractionDigits: 0 }).format(n || 0);

export default function Hpp() {
  const [kata, setKata] = useState("");
  const [hasil, setHasil] = useState(null);
  const [detail, setDetail] = useState(null);
  const [bulan, setBulan] = useState(() => tanggalLokalISO().slice(0, 7));
  const [rekap, setRekap] = useState(null);
  const [galat, setGalat] = useState("");
  const [sibuk, setSibuk] = useState(false);

  const cari = useCallback(async (e) => {
    e?.preventDefault();
    if (kata.trim().length < 2) return;
    setSibuk(true); setGalat(""); setDetail(null);
    try {
      const { data } = await api.get("/hpp/cari", { params: { kata: kata.trim() } });
      setHasil(data.items || []);
    } catch (err) { setGalat(apiErrorMessage(err)); }
    finally { setSibuk(false); }
  }, [kata]);

  const buka = useCallback(async (faId) => {
    setSibuk(true); setGalat("");
    try {
      const { data } = await api.get(`/hpp/fa/${faId}`);
      setDetail(data);
    } catch (err) { setGalat(apiErrorMessage(err)); }
    finally { setSibuk(false); }
  }, []);

  useEffect(() => {
    api.get("/hpp/rekap", { params: { bulan } })
      .then(({ data }) => setRekap(data))
      .catch((err) => setGalat(apiErrorMessage(err)));
  }, [bulan]);

  return (
    <div className="max-w-5xl space-y-6" data-testid="hpp-page">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">HPP & Margin</h1>
        {/* ⛔ K139 — keterangan ini bagian dari keputusannya, bukan hiasan. */}
        <p className="mt-1 text-sm text-warning-soft-foreground">
          Angka di halaman ini adalah biaya <b>LANGSUNG</b> (material + upah
          borongan jahit) — <b>bukan</b> biaya penuh. Overhead sengaja tidak
          dibagi rata. Margin di sini = margin <b>KOTOR</b>.
        </p>
      </div>

      {galat && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">{galat}</div>
      )}

      <form onSubmit={cari} className="flex gap-2">
        <input value={kata} onChange={(e) => setKata(e.target.value)}
               placeholder="Nomor FA atau nama customer…"
               className="w-full rounded-lg border border-border bg-background px-4 py-2 text-sm text-foreground"
               data-testid="hpp-cari-input" />
        <button type="submit" disabled={sibuk}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
          {sibuk ? "Mencari…" : "Cari"}
        </button>
      </form>

      {hasil && !detail && (
        <div className="space-y-2">
          {hasil.length === 0 && (
            <p className="text-sm text-muted-foreground">Tidak ada FA yang cocok.</p>
          )}
          {hasil.map((f) => (
            <button key={f.id} type="button" onClick={() => buka(f.id)}
                    className="block w-full rounded-lg border border-border bg-card p-3 text-left text-sm hover:bg-muted">
              <span className="font-medium text-foreground">{f.fa_number}</span>
              <span className="ml-2 text-muted-foreground">{f.customer_name || "—"}</span>
            </button>
          ))}
        </div>
      )}

      {detail && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="hpp-detail">
          <button type="button" onClick={() => setDetail(null)}
                  className="text-sm text-muted-foreground hover:text-foreground">← kembali</button>
          <p className="text-lg font-semibold text-foreground">{detail.fa.fa_number}
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              {detail.fa.qty_atasan} atasan · {detail.fa.qty_celana || 0} celana
            </span>
          </p>

          {/* ⛔ Temuan DI ATAS angka. */}
          {detail.temuan?.length > 0 && (
            <div className="rounded border border-warning-soft-border bg-warning-soft p-3 text-xs text-warning-soft-foreground">
              {detail.temuan.map((t, i) => <p key={i}>⚠ {t}</p>)}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[["Harga jual", detail.fa.harga_jual_idr],
              ["Material", detail.material_idr],
              ["Upah jahit", detail.upah_jahit_idr],
              ["HPP langsung", detail.hpp_idr],
              ["Margin kotor", detail.margin_idr]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">{l}</p>
                <p className={`text-sm font-semibold tabular-nums ${v < 0 ? "text-destructive" : "text-foreground"}`}>{rupiah(v)}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            Margin kotor: <b className={detail.margin_idr < 0 ? "text-destructive" : "text-foreground"}>
              {detail.margin_pct === null ? "— (FA belum punya harga jual)" : `${detail.margin_pct}%`}
            </b> dari harga jual.
          </p>

          {detail.rincian_wo?.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="border-b text-xs text-muted-foreground">
                <tr><th className="py-1 text-left">WO</th><th className="text-right">Material WO</th><th className="text-right">Alokasi FA ini</th><th className="text-right">Pengeluaran bahan</th></tr>
              </thead>
              <tbody>
                {detail.rincian_wo.map((w) => (
                  <tr key={w.wo_id} className="border-b border-border">
                    <td className="py-1">{w.wo_number || "—"}</td>
                    <td className="text-right tabular-nums">{rupiah(w.material_wo)}</td>
                    <td className="text-right tabular-nums">{rupiah(w.alokasi_fa)}</td>
                    <td className="text-right">{w.jumlah_mi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs text-muted-foreground">
              FA ini belum masuk WO mana pun — biaya materialnya masih nol
              karena memang belum ada yang keluar gudang untuknya.
            </p>
          )}
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            Biaya reject/remake ikut otomatis.
            <Hint>Material pengganti dan upah pengulangan masuk lewat pengeluaran bahan dan serah terima tambahan di WO yang sama.</Hint>
          </p>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-medium text-foreground">Rekap biaya langsung per bulan</p>
          <input type="month" value={bulan} onChange={(e) => setBulan(e.target.value)}
                 className="rounded border border-border bg-background px-2 py-1 text-sm text-foreground" />
        </div>
        {rekap && (
          <>
            {rekap.temuan?.length > 0 && (
              <div className="rounded border border-warning-soft-border bg-warning-soft p-2 text-xs text-warning-soft-foreground">
                {rekap.temuan.map((t, i) => <p key={i}>⚠ {t}</p>)}
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              {[["Material", rekap.material_idr], ["Upah jahit", rekap.upah_jahit_idr],
                ["Total", rekap.total_idr]].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-border p-3">
                  <p className="text-xs text-muted-foreground">{l}</p>
                  <p className="text-sm font-semibold tabular-nums text-foreground">{rupiah(v)}</p>
                </div>
              ))}
            </div>
            {/* ⚠ Dasarnya dari backend, ditampilkan apa adanya — dua layar
                yang menjelaskan basis dengan kalimat sendiri akan berselisih. */}
            <p className="text-xs text-muted-foreground">{rekap.dasar}</p>
          </>
        )}
      </div>
    </div>
  );
}
